import React, { useContext } from 'react'
import { useRouter } from 'next/navigation'
import { X } from 'lucide-react'

import { useIsDesktop } from '@rewardops/ui'

import { BEXContext } from '~/context/bex-context'
import { CloseBtn } from './banner-close-btn'

type GenericStickyBannerProps = {
  content: {
    id: string
    title: string
    subtitle: string
    textColor: string
    backgroundColor: string
    isBex: boolean | null
    link: {
      id: string
      isRelativeUrl?: boolean
      name: string
      to: string
    }
    image: {
      file: {
        url: string
        filename: string
      }
    }
  }
}

const GenericStickyBanner: React.FC<GenericStickyBannerProps> = ({
  content,
}: GenericStickyBannerProps) => {
  const [isClosed, setIsClosed] = React.useState<boolean>(false)

  const router = useRouter()

  const isDesktop = useIsDesktop()
  const isBEXInstalled = useContext(BEXContext) && Boolean(content?.isBex)

  const colorStyles = {
    color: content?.textColor ?? '#FFFFFF',
    backgroundColor: content?.backgroundColor ?? '#000000',
  }

  const onClick = () => {
    if (!content?.link?.isRelativeUrl) {
      return window.open(content?.link?.to)
    }

    router.push(content?.link?.to)
  }

  if (isClosed || isBEXInstalled) {
    return null
  }

  return (
    <div className="col-span-2 mx-auto mb-[100px] w-full max-w-[1140px] lg:h-[166px]">
      <div
        className="relative flex h-full w-full flex-col items-center justify-between rounded-lg lg:flex-row"
        style={colorStyles}
      >
        {!isDesktop && <CloseBtn setIsClosed={setIsClosed} />}
        <div className="my-8 flex flex-1 flex-col justify-center gap-4 px-4 text-center lg:my-0 lg:flex-row lg:px-0 lg:text-left">
          <div className="flex max-w-[550px] flex-col gap-3">
            <div className="line-clamp-2 text-4xl lg:line-clamp-1">
              {content?.title}
            </div>
            <div className="line-clamp-3 text-lg lg:line-clamp-1">
              {content?.subtitle}
            </div>
          </div>
          <button
            id={content?.link?.id}
            className="h-[56px] w-[227px] self-center rounded-full bg-tenant-primary-color text-white"
            onClick={onClick}
          >
            {content?.link?.name}
          </button>
        </div>

        <div className="relative h-[120px] w-full self-end lg:h-full lg:w-[285px]">
          {isDesktop && <CloseBtn setIsClosed={setIsClosed} />}
          <img
            id={content?.image?.file?.filename}
            src={content?.image?.file?.url}
            className="h-full w-full rounded-b-lg object-cover lg:rounded-r-lg lg:rounded-bl-none"
            alt={`generic-sticky-banner-${content?.image?.file?.filename}`}
          />
        </div>
      </div>
    </div>
  )
}

export { GenericStickyBanner }
